import { ref } from 'vue';
import axios from 'axios';
import { DateTime } from 'luxon';
import { BatchProcessStatus, GetConfigV1Response, SourceType } from '@ilteducation/babelfish-types';
import eventBus from '@/foundations/services/eventBus';

export type BatchStatus = BatchProcessStatus & {
  msg: string;
};

export const useBabelfish = () => {
  const baseLanguage = ref<string>('en-gb');
  const lokaliseProjectId = ref<string>('');
  const isLoading = ref(true);
  const triggerInProgress = ref(false);
  const resourcesConfigs = ref<GetConfigV1Response['resources']>([]);

  axios.get<GetConfigV1Response>(`${process.env.VUE_APP_TRANSLATIONS_API_URL_CONFIG}`).then((response) => {
    const config = response.data;

    baseLanguage.value = config.baseLanguage;
    resourcesConfigs.value = config.resources;
    lokaliseProjectId.value = config.lokaliseProjectId;

    isLoading.value = false;
  });

  const triggerBatchJob = async () => {
    try {
      triggerInProgress.value = true;
      await axios.post(`${process.env.VUE_APP_TRANSLATIONS_API_URL_TRIGGER_JOB}`);
      eventBus.$emit('snackbar.open', `Nightly job triggered successfully`);
    } finally {
      triggerInProgress.value = false;
    }
  };

  const batchJobStatus = async (): Promise<BatchStatus> => {
    const res = await axios.get<BatchProcessStatus>(`${process.env.VUE_APP_TRANSLATIONS_API_URL_STATUS}`);
    const status: BatchStatus = {
      ...res.data,
      msg: '',
    };

    const t = DateTime.fromISO(status.timestamp);
    switch (status.status) {
      case 'triggered':
        status.msg = `Job triggered ${t.toRelative()}.`;
        break;
      case 'started':
        status.msg = `Job running. Started ${t.toRelative()}.`;
        break;
      case 'ended':
        status.msg = `Last job finished ${t.toRelative()}.`;
        break;
      default:
        break;
    }
    return status;
  };

  const orderedLanguages = (sourceType: SourceType): string[] => {
    const langs = [baseLanguage.value];
    const resourceConfig = resourcesConfigs.value.find((r) => r.sourceType === sourceType);
    if (!resourceConfig) {
      return langs;
    }
    langs.push(...resourceConfig.marketLanguages);
    const sortedLangs = resourceConfig.availableLanguages.sort();
    sortedLangs.forEach((lang) => {
      if (!langs.includes(lang)) {
        langs.push(lang);
      }
    });
    return langs;
  };

  return {
    orderedLanguages,
    baseLanguage,
    resourcesConfigs,
    lokaliseProjectId,
    isLoading,
    triggerBatchJob,
    triggerInProgress,
    batchJobStatus,
  };
};
